import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, TextField } from '@material-ui/core';
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTokens, INK } from "../../styles/tokens";
import { Section } from "../../components/ui";

const useStyles = makeStyles((theme) => {
    const t = getTokens(theme.palette.type, theme.palette.primary.main);
    return {
        root: {
            height: "100%",
            boxSizing: "border-box",
        },
        range: {
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
        },
        rangeSep: {
            fontSize: 13,
            color: t.textMuted,
        },
        picker: {
            width: 150,
            [theme.breakpoints.down("xs")]: { flex: "1 1 120px", width: "auto" },
        },
    };
});

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

/** Opções do gráfico derivadas dos tokens (marca + superfícies). */
export const buildOptions = (t) => ({
    responsive: true,
    plugins: {
        legend: {
            position: 'top',
            display: false,
        },
        title: {
            display: true,
            text: 'Tickets',
            position: 'left',
            color: t.textSecondary,
            font: { family: t.font, size: 12, weight: '500' },
        },
        tooltip: {
            backgroundColor: t.mode === "dark" ? t.paper : INK[900],
            titleColor: t.mode === "dark" ? t.text : "#fff",
            bodyColor: t.mode === "dark" ? t.text : "#fff",
            titleFont: { family: t.font, size: 12, weight: '600' },
            bodyFont: { family: t.font, size: 12 },
            padding: 10,
            cornerRadius: 6,
            displayColors: false,
        },
        datalabels: {
            display: true,
            anchor: 'start',
            offset: -30,
            align: "start",
            color: t.brandOn,
            font: {
                family: t.font,
                size: 12,
                weight: "600",
            },
        }
    },
    scales: {
        x: {
            grid: { display: false, drawBorder: true, borderColor: t.line },
            ticks: { color: t.textSecondary, font: { family: t.font, size: 12 } },
        },
        y: {
            beginAtZero: true,
            grid: { color: t.line, drawBorder: false },
            ticks: { color: t.textSecondary, font: { family: t.font, size: 12 }, precision: 0 },
        },
    },
});

export const ChatsUser = () => {
    const classes = useStyles();
    const theme = useTheme();
    const t = useMemo(
        () => getTokens(theme.palette.type, theme.palette.primary.main),
        [theme.palette.type, theme.palette.primary.main]
    );
    const options = useMemo(() => buildOptions(t), [t]);
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [] });
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    const dataCharts = {

        labels: ticketsData && ticketsData?.data.length > 0 && ticketsData?.data.map((item) => item.nome),
        datasets: [
            {
                data: ticketsData?.data.length > 0 && ticketsData?.data.map((item, index) => {
                    return item.quantidade
                }),
                backgroundColor: t.brand,
                hoverBackgroundColor: t.brandHover,
                borderRadius: 6,
                maxBarThickness: 40,
            },

        ],
    };

    const handleGetTicketsInformation = async () => {
        try {

            const { data } = await api.get(`/dashboard/ticketsUsers?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`);
            setTicketsData(data);
        } catch (error) {
            toast.error('Erro ao buscar informações dos tickets');
        }
    }

    const rangePicker = (value, onChange, label) => (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
            <DatePicker
                value={value}
                onChange={onChange}
                renderInput={(params) => (
                    <TextField
                        size="small"
                        variant="outlined"
                        {...params}
                        inputProps={{ ...params.inputProps, "aria-label": label }}
                        className={classes.picker}
                    />
                )}
            />
        </LocalizationProvider>
    );

    return (
        <Section
            className={classes.root}
            title={i18n.t("dashboard.users.totalCallsUser")}
            actions={
                <div className={classes.range}>
                    {rangePicker(initialDate, (newValue) => { setInitialDate(newValue) }, i18n.t("dashboard.date.initialDate"))}
                    <span className={classes.rangeSep}>até</span>
                    {rangePicker(finalDate, (newValue) => { setFinalDate(newValue) }, i18n.t("dashboard.date.finalDate"))}
                    <Button onClick={handleGetTicketsInformation} variant="outlined">Filtrar</Button>
                </div>
            }
        >
            <Bar options={options} data={dataCharts} style={{ maxWidth: '100%', maxHeight: '280px', }} />
        </Section>
    );
}
